import { initContract } from '@ts-rest/core';
import { initQueryClient } from '@ts-rest/react-query';
import Cookies from 'js-cookie';
import jwtDecode, { JwtPayload } from 'jwt-decode';

import {
  adminBatchContract,
  adminDeclarationsContract,
  adminTransitContract,
  adminTsdContract,
  adminVatReportsContract,
  aiModuleContract,
  batchesArchiveContract,
  batchesCoreContract,
  batchesFinalizationContract,
  batchesReportsContract,
  batchesWorkflowContract,
  brokerContract,
  catalogsContract,
  chartContract,
  commentContract,
  customerContract,
  customsErrorContract,
  customsNotificationsContract,
  customsSimulatorContract,
  customsTradeContract,
  declarantContract,
  declarationsCrudContract,
  declarationsCustomsContract,
  declarationsDocumentsContract,
  declarationsListingContract,
  declarationsReportsContract,
  dutyContract,
  dutyExportContract,
  forwarderContract,
  idmsContract,
  importerContract,
  itemAiChatContract,
  invitationContract,
  madbContract,
  multilingualCodeAdminContract,
  multilingualCodeContract,
  nomenclatureContract,
  measureSolutionsContract,
  reportsContract,
  rolesContract,
  settingsContract,
  transactionTypeContract,
  transitContract,
  tsdContract,
  userContract,
  usersContract,
} from '@e-origin/contracts';
import { decrypt } from '@e-origin/crypto-wasm/browser-loader';
import { X_VISITOR_ID } from '@e-origin/shared';

import {
  ADMIN_AUTH_COOKIE_NAME,
  API_ENCODING_KEY,
  AUTH_COOKIE_NAME,
  getAdminBase,
  isAdminBuild,
  MILLISECONDS_IN_SECOND,
  TOKEN_EXPIRATION_BUFFER_SECONDS,
} from '@constants';

const getBaseUrl = () =>
  `${['localhost', '0.0.0.0', '127.0.0.1'].includes(window.location.hostname) ? ((import.meta as any).env.VITE_API_URL ?? '') : ''}/api/v1`;

// eslint-disable-next-line max-statements
const apiFetcher = async ({ path, method, headers, body }: any) => {
  const isAdminRoute = isAdminBuild || ((import.meta as any).env.DEV && window.location.pathname.startsWith('/admin'));
  const cookieName = isAdminRoute ? ADMIN_AUTH_COOKIE_NAME : AUTH_COOKIE_NAME;
  const redirectUrl = isAdminRoute ? `${getAdminBase()}/auth` : '/auth';
  const token = Cookies.get(cookieName);

  const requestHeaders: Record<string, string> = { ...headers };

  const visitorId = Cookies.get(X_VISITOR_ID);
  if (visitorId) {
    requestHeaders[X_VISITOR_ID] = visitorId;
  }

  if (token) {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.exp * MILLISECONDS_IN_SECOND < Date.now() + TOKEN_EXPIRATION_BUFFER_SECONDS * MILLISECONDS_IN_SECOND) {
        Cookies.set(cookieName, '');
      } else {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    } catch {
      Cookies.set(cookieName, '');
      window.location.href = redirectUrl;
      return { status: 401, body: null, headers: new Headers() };
    }
  }

  const response = await fetch(path, {
    method,
    headers: requestHeaders,
    body: body ?? undefined,
  });

  const responseHeaders = response.headers;
  const jwtToken = responseHeaders.get('x-jwt-token');
  if (jwtToken) {
    Cookies.set(AUTH_COOKIE_NAME, jwtToken);
  }

  let responseBody: unknown;
  const contentType = responseHeaders.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    responseBody = await response.json();
    const data = (responseBody as any)?.data;
    if (typeof data === 'string') {
      const encryptedBytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
      const decryptedString = await decrypt(encryptedBytes, API_ENCODING_KEY);
      const decodedData = JSON.parse(decryptedString);
      if (decodedData) {
        (responseBody as any).data = decodedData;
      }
    }
  } else if (contentType.includes('text/')) {
    responseBody = await response.text();
  } else {
    responseBody = await response.blob();
  }

  return { status: response.status, body: responseBody, headers: responseHeaders };
};

const c = initContract();
const contract = c.router({
  adminBatch: adminBatchContract,
  adminDeclarations: adminDeclarationsContract,
  adminTransit: adminTransitContract,
  admin: usersContract,
  aiModule: aiModuleContract,
  catalogs: catalogsContract,
  chart: chartContract,
  customer: customerContract,
  customsError: customsErrorContract,
  customsNotifications: customsNotificationsContract,
  declarant: declarantContract,
  duty: dutyContract,
  dutyExport: dutyExportContract,
  forwarder: forwarderContract,
  idms: idmsContract,
  invitation: invitationContract,
  madb: madbContract,
  multilingualCode: multilingualCodeContract,
  multilingualCodeAdmin: multilingualCodeAdminContract,
  nomenclature: nomenclatureContract,
  reports: reportsContract,
  roles: rolesContract,
  settings: settingsContract,
  transactionType: transactionTypeContract,
  measureSolutions: measureSolutionsContract,
  customsSimulator: customsSimulatorContract,
  user: userContract,
  transit: transitContract,
  tsd: tsdContract,
  comment: commentContract,
  adminTsd: adminTsdContract,
  adminVatReports: adminVatReportsContract,
  customsTrade: customsTradeContract,
  broker: brokerContract,
  importer: importerContract,
  itemAiChat: itemAiChatContract,
  batchesCore: batchesCoreContract,
  declarations: declarationsListingContract,
  declarationsCrud: declarationsCrudContract,
  declarationsCustoms: declarationsCustomsContract,
  declarationsReports: declarationsReportsContract,
  declarationsDocuments: declarationsDocumentsContract,
  batchesArchive: batchesArchiveContract,
  batchesFinalization: batchesFinalizationContract,
  batchesWorkflow: batchesWorkflowContract,
  batchesReports: batchesReportsContract,
});

export const apiClient = initQueryClient(contract, {
  baseUrl: getBaseUrl(),
  api: apiFetcher,
});
